import { useCallback, useEffect, useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

const INTERSECTION_OBSERVER_CONFIG = {
  threshold: [0, 0.1, 0.25, 0.5, 0.75, 1.0] as Array<number>,
  rootMargin: '-80px 0px -30% 0px',
} as const;

const SCROLL_CONFIG = {
  bottomThreshold: 100,
  debounceDelay: 16,
} as const;

type UseActiveElementOptions = {
  threshold?: number;
  rootMargin?: string;
  bottomThreshold?: number;
  debounceDelay?: number;
};

/**
 * Custom hook to track which element is currently active based on scroll position
 * Uses IntersectionObserver to detect visible elements and scroll events for edge cases
 *
 * @param elementIds - Array of element IDs to track
 * @param options - Configuration options for intersection observer and scroll behavior
 * @returns The ID of the currently active element
 */
export const useActiveElement = (elementIds: Array<string>, options: UseActiveElementOptions = {}) => {
  const [activeElement, setActiveElement] = useState<string>('');
  const elementsRef = useRef<Set<Element>>(new Set());

  const {
    threshold = INTERSECTION_OBSERVER_CONFIG.threshold,
    rootMargin = INTERSECTION_OBSERVER_CONFIG.rootMargin,
    bottomThreshold = SCROLL_CONFIG.bottomThreshold,
    debounceDelay = SCROLL_CONFIG.debounceDelay,
  } = options;

  const getHeaderHeight = useCallback(() => {
    const headerHeight = document.documentElement.style.getPropertyValue('--order-form-header-height');
    return headerHeight ? parseInt(headerHeight.replace('px', ''), 10) : 80;
  }, []);

  const selectActiveEntry = useCallback((sortedEntries: Array<IntersectionObserverEntry>, lastElementId: string) => {
    if (sortedEntries.length === 1) {
      return sortedEntries[0];
    }

    // Prefer last element when it's the second visible element
    // This improves UX for end-of-content navigation
    const firstEntry = sortedEntries[0];
    const secondEntry = sortedEntries[1];

    if (secondEntry?.target.id === lastElementId) {
      return secondEntry;
    }

    return firstEntry;
  }, []);

  const handleIntersection = useCallback(
    (entries: Array<IntersectionObserverEntry>) => {
      try {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);

        if (visibleEntries.length > 0) {
          const headerHeight = getHeaderHeight();
          const viewportHeight = window.innerHeight;
          const effectiveViewportHeight = viewportHeight - headerHeight;
          const sixtyPercentThreshold = headerHeight + effectiveViewportHeight * 0.6;

          // Check if we're dealing with the last element
          const lastElementId = elementIds[elementIds.length - 1];
          const lastElementEntry = visibleEntries.find((entry) => entry.target.id === lastElementId);

          // Special handling for last element - select if fully visible
          if (lastElementEntry) {
            const lastRect = lastElementEntry.boundingClientRect;
            const isFullyVisible = lastRect.top >= headerHeight && lastRect.bottom <= viewportHeight;

            if (isFullyVisible) {
              setActiveElement(lastElementId);
              return;
            }
          }

          // Filter entries that have their top in the top 60% of viewport
          const entriesInTopZone = visibleEntries.filter((entry) => {
            const rect = entry.boundingClientRect;
            return rect.top <= sixtyPercentThreshold;
          });

          const selectedEntries = entriesInTopZone.length > 0 ? entriesInTopZone : visibleEntries;

          // Sort entries by their order in elementIds to maintain document order
          const sortedEntries = selectedEntries.sort((a, b) => {
            const indexA = elementIds.indexOf(a.target.id);
            const indexB = elementIds.indexOf(b.target.id);
            return indexA - indexB;
          });

          const selectedEntry = sortedEntries.length > 0 ? selectActiveEntry(sortedEntries, lastElementId) : null;

          if (selectedEntry?.target.id) {
            setActiveElement(selectedEntry.target.id);
          }
        } else {
          // Fallback: find which element should be active based on scroll position
          const headerHeight = getHeaderHeight();

          // Check if any element spans across the header line - it should be active immediately
          const spanningElement = elementIds.find((id) => {
            const element = document.getElementById(id);
            if (element) {
              const rect = element.getBoundingClientRect();
              return rect.top <= headerHeight && rect.bottom > headerHeight;
            }
            return false;
          });

          if (spanningElement) {
            setActiveElement(spanningElement);
            return;
          }

          // If no spanning element, find the closest element that's still visible
          let closestElementId: string | null = null;
          let smallestDistance = Infinity;

          elementIds.forEach((id) => {
            const element = document.getElementById(id);
            if (element) {
              const rect = element.getBoundingClientRect();
              const distanceFromTop = Math.abs(rect.top - headerHeight);

              if (distanceFromTop < smallestDistance && rect.bottom > headerHeight) {
                smallestDistance = distanceFromTop;
                closestElementId = element.id;
              }
            }
          });

          if (closestElementId) {
            setActiveElement(closestElementId);
          }
        }
      } catch (error) {
        console.warn('Error handling intersection observer entries:', error);
      }
    },
    [elementIds, getHeaderHeight, selectActiveEntry],
  );

  const handleScroll = useCallback(() => {
    try {
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;

      if (documentHeight - scrollPosition < bottomThreshold && elementIds.length > 0) {
        const lastElement = elementIds[elementIds.length - 1];
        setActiveElement(lastElement);
      }
    } catch (error) {
      console.warn('Error handling scroll event:', error);
    }
  }, [elementIds, bottomThreshold]);

  const debouncedHandleScroll = useDebounceCallback(handleScroll, debounceDelay);

  useEffect(() => {
    const ref = elementsRef.current;
    if (elementIds.length === 0) {
      setActiveElement('');
      return;
    }

    try {
      const observer = new IntersectionObserver(handleIntersection, {
        threshold,
        rootMargin,
      });

      ref.clear();

      elementIds.forEach((elementId) => {
        const element = document.getElementById(elementId);
        if (element) {
          observer.observe(element);
          ref.add(element);
        }
      });

      window.addEventListener('scroll', debouncedHandleScroll, { passive: true });

      return () => {
        observer.disconnect();
        window.removeEventListener('scroll', debouncedHandleScroll);
        ref.clear();
      };
    } catch (error) {
      console.error('Error setting up intersection observer:', error);
    }
  }, [elementIds, handleIntersection, debouncedHandleScroll, threshold, rootMargin]);

  return activeElement;
};
